import {
  LiquidityPages,
  type LiquidityPageId,
} from './liquidityPages';
import {
  appendPageNameQueryParam,
  formatFundUrl,
} from '../../../utilities/urlBuilder';

export { appendPageNameQueryParam } from '../../../utilities/urlBuilder';
export {
  LiquidityPages,
  type LiquidityPageId,
  type LiquidityPageKey,
} from './liquidityPages';

export const getLiquidityOverviewMonitorURL = (
  positionDate?: string,
  fundTypes?: string,
  raptorPlatformId?: string,
  deceased?: boolean,
) => {
  const params: string[] = [];

  if (deceased) params.push(`status=deceased`);
  if (fundTypes?.length) params.push(`schemas=${fundTypes}`);
  if (raptorPlatformId?.length)
    params.push(`raptor_platform_id=${raptorPlatformId}`);

  const queryString = params.length ? `?${params.join('&')}` : '';
  const positionDatePath = positionDate ? `/${positionDate}` : '';

  return `liquidity-monitor${positionDatePath}${queryString}`;
};

/**
 * Stored aggregated report endpoints only (`most_recently_stored_report_data_agg` /
 * `stored_report_data_agg`). Appends `page_name=<id>` when `liquidityPage` is set
 * so the backend can pick the model. Not used for `liquidity-monitor` or other routes.
 */
export function getStoredAggregatedReportURL(
  fundId: string,
  reportKey: string,
  positionDate?: string | null,
  liquidityPage?: LiquidityPageId,
): string {
  const path = positionDate
    ? `stored_report_data_agg/${fundId}/${reportKey}/${positionDate}`
    : `most_recently_stored_report_data_agg/${fundId}/${reportKey}`;
  return liquidityPage ? appendPageNameQueryParam(path, liquidityPage) : path;
}

/** Path segment after `fundId` for stored-aggregated liquidity report URLs. */
export const STORED_AGGREGATED_REPORT = {
  SUB_RED_RISK_DATA: 'sub_red_risk_data',
  CPL_RISK: 'cpl_risk',
  MOAB_DATA2: 'moab_data2',
  LRST_FMV_KEY_DATA: 'lrst_fmv_key_data',
  /** Single aggregate blob: `stress_tests` + `lrst_keys` (no per-scenario URL). */
  VARIABLE_MARKET_VOLUME_LIQUIDATION_COSTS:
    'variable_market_volume_liquidation_costs',
} as const;

/** UCITS Subscriptions / Redemptions page (`sub_red_risk_data`). */
export const getSubscriptionsRedemptionsURL = (
  fundId: string,
  positionDate?: string,
) =>
  getStoredAggregatedReportURL(
    fundId,
    STORED_AGGREGATED_REPORT.SUB_RED_RISK_DATA,
    positionDate,
    LiquidityPages.SUBSCRIPTION_REDEMPTIONS,
  );

/** LASDAB — CPL risk page (`cpl_risk`). */
export const getLasdabCplRiskReportURL = (
  fundId: string,
  positionDate?: string,
) =>
  getStoredAggregatedReportURL(
    fundId,
    STORED_AGGREGATED_REPORT.CPL_RISK,
    positionDate,
    LiquidityPages.LASDAB,
  );

/** MOAB page (`moab_data2`). */
export const getMoabReportURL = (fundId: string, positionDate?: string) =>
  getStoredAggregatedReportURL(
    fundId,
    STORED_AGGREGATED_REPORT.MOAB_DATA2,
    positionDate,
    LiquidityPages.MAXIMUM_ONE_DAY_ACHIEVABLE_BALANCE,
  );

/** Variable market volume liquidation costs — full report (`stress_tests`, `lrst_keys`). */
export const getVariableMarketVolumeLiquidationCostsReportURL = (
  fundId: string,
  positionDate?: string,
) =>
  getStoredAggregatedReportURL(
    fundId,
    STORED_AGGREGATED_REPORT.VARIABLE_MARKET_VOLUME_LIQUIDATION_COSTS,
    positionDate,
    LiquidityPages.VARIABLE_MARKET_VOLUME_LIQUIDATION_COSTS,
  );

/** RST Fixed — scenario dropdown options (`lrst_fmv_key_data`, most recent only). */
export const getRstFixedLrstFmvKeyDataURL = (
  fundId: string,
  positionDate?: string | null,
) =>
  getStoredAggregatedReportURL(
    fundId,
    STORED_AGGREGATED_REPORT.LRST_FMV_KEY_DATA,
    positionDate,
    LiquidityPages.LIQUIDITY_RST_FIXED_MARKET_VOLUME,
  );

/** RST Fixed — main report for a chosen scenario key. */
export const getRstFixedScenarioReportURL = (
  fundId: string,
  scenarioKey: string,
  positionDate?: string | null,
) =>
  getStoredAggregatedReportURL(
    fundId,
    scenarioKey,
    positionDate,
    LiquidityPages.LIQUIDITY_RST_FIXED_MARKET_VOLUME,
  );

/** Path segments for `aggregate_liquidity_risk_report_data` (Bottle). */
export const AGGREGATE_LIQUIDITY_RISK_REPORT_PATH = {
  LIQUIDATION_TIME_COST: 'aggregate_liquidity_risk_report_data/2',
  LIQUIDITY_RISK_OVERVIEW: 'aggregate_liquidity_risk_report_data/3',
} as const;

const LIQUIDITY_RISK_EVOLUTION_BASE = 'aggregate_lqdty_review_data';
const MARKET_LIQUIDITY_BID_ASK_BASE = 'fund_liquidity_bid_ask_data';

/** Liquidation risk overview (`aggregate_liquidity_risk_report_data/3`). */
export const getLiquidationRiskOverviewURL = (
  fundId: string,
  positionDate?: string,
) =>
  appendPageNameQueryParam(
    formatFundUrl(
      AGGREGATE_LIQUIDITY_RISK_REPORT_PATH.LIQUIDITY_RISK_OVERVIEW,
      fundId,
      positionDate,
    ),
    LiquidityPages.LIQUIDATION_RISK_OVERVIEW,
  );

/** RST variable market volume (`aggregate_liquidity_risk_report_data/2`). */
export const getRstVariableReportURL = (
  fundId: string,
  positionDate?: string,
) =>
  appendPageNameQueryParam(
    formatFundUrl(
      AGGREGATE_LIQUIDITY_RISK_REPORT_PATH.LIQUIDATION_TIME_COST,
      fundId,
      positionDate,
    ),
    LiquidityPages.LIQUIDITY_RST_VARIABLE_MARKET_VOLUME,
  );

/** Liquidity risk evolution between two dates. */
export const getLiquidityRiskEvolutionURL = (
  fundId: string,
  date1: string,
  date2: string,
) =>
  appendPageNameQueryParam(
    `${LIQUIDITY_RISK_EVOLUTION_BASE}/${fundId}/${date1}/${date2}`,
    LiquidityPages.LIQUIDITY_RISK_EVOLUTION,
  );

/** Market liquidity bid/ask table for one fund. */
export const getMarketLiquidityBidAskURL = (
  fundId: string,
  positionDate?: string | null,
) =>
  formatFundUrl(
    MARKET_LIQUIDITY_BID_ASK_BASE,
    fundId,
    positionDate ?? undefined,
  );

export function getLiquidityModelBreakdownURL(input: {
  fundId: string;
  positionDate: string;
  participationRate?: string;
}): string {
  const { fundId, positionDate, participationRate } = input;
  return participationRate
    ? `liquidity_model_breakdown_for_date/${fundId}/${positionDate}/${participationRate}`
    : `liquidity_model_breakdown_for_date/${fundId}/${positionDate}`;
}

export function getLiquidityModelEvolutionURL(input: {
  fundId: string;
  startDate: string;
  endDate: string;
  participationRate?: string;
}): string {
  const { fundId, startDate, endDate, participationRate } = input;
  return participationRate
    ? `liquidity_model_breakdown_for_date_range/${fundId}/${startDate}/${endDate}/${participationRate}`
    : `liquidity_model_breakdown_for_date_range/${fundId}/${startDate}/${endDate}`;
}
